import React, { useState, useRef } from "react";
import { Form, Button, Card, Row, Col, Spinner } from "react-bootstrap";
import { uploadDocumentRequest, uploadDocumentFilesRequest, deleteDocumentRequest } from "@/services";
import DocumentFiles from '../document_files';
import { uploadEachFile, describeUploadFailures } from '../upload_utils';
import { BsTrash } from "react-icons/bs";
import { useParams } from "react-router-dom";

const BankStatementsUploadForm = ({
  doc,
  reloadDocumentsList,
  documentsList = [],
  validationError,
  onUploadingChange,
}) => {
  const { applicationId: applicationUuid } = useParams();
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [newBank, setNewBank] = useState({ bank_name: "", account_number: "" });
  const [files, setFiles] = useState([]);
  const [error, setError] = useState("");
  const [cardError, setCardError] = useState({ id: null, message: "" });
  const fileInputsRef = useRef({});

  /** STEP 1 — Get existing banks from the document list */
  const existingBanks = documentsList.filter(
    (d) => d.document_type === doc.document_type
  );

  /** STEP 2 — Handle Add Bank Button */
  const handleAddClick = () => {
    setAdding(true);
    setError("");
  };

  const handleCancel = () => {
    setAdding(false);
    setNewBank({ bank_name: "", account_number: "" });
    setError("");
  };

  /** STEP 3 — Handle new bank form input */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewBank((prev) => ({ ...prev, [name]: value }));
  };

  /** STEP 4 — Save new bank (metadata only) */
  const handleSaveBank = async () => {
    if (!newBank.bank_name || !newBank.account_number) {
      setError("Please enter both Bank Name and Account Number.");
      return;
    }

    setSaving(true);
    setError("");

    try {
      const formData = new FormData();
      formData.append("loan_id", applicationUuid);
      formData.append("document_type_id", doc.document_type);
      formData.append("metadata", JSON.stringify(newBank));

      const res = await uploadDocumentRequest(formData);

      if (res?.success) {
        await reloadDocumentsList();
        setAdding(false);
        setNewBank({ bank_name: "", account_number: "" });
      } else {
        setError("Error saving bank. Try again.");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to save bank info. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleUploadFiles = async (e, bankDoc) => {
    const files = Array.from(e.target.files);

    if (!files.length) {
      setCardError({ id: bankDoc.id, message: "Please select files to upload." });
      return;
    }

    setUploadingId(bankDoc.id);
    onUploadingChange?.(true);
    setCardError({ id: null, message: "" });

    try {
      const failures = await uploadEachFile(files, (file) => {
        const uploadData = new FormData();
        uploadData.append("file", file);
        return uploadDocumentFilesRequest(bankDoc.id, uploadData);
      });
      setCardError({ id: bankDoc.id, message: describeUploadFailures(failures, files.length) });

      setFiles([]);

      // ✅ Reset only this specific input
      if (fileInputsRef.current[bankDoc.id]) {
        fileInputsRef.current[bankDoc.id].value = "";
      }
      await reloadDocumentsList();
    } catch (err) {
      console.error(err);
      setCardError({ id: bankDoc.id, message: "File upload failed. Try again." });
    } finally {
      setUploadingId(null);
      onUploadingChange?.(false);
    }
  };


  /** STEP 6 — Delete Entire Bank Document */
  const handleDeleteBank = async (bankDoc) => {
    if (!window.confirm(`Delete ${bankDoc.metadata?.bank_name}? This will remove all statements too.`))
      return;

    try {
      setDeleting(bankDoc.id);
      const res = await deleteDocumentRequest(bankDoc.uuid, applicationUuid); // Backend deletes document + files
      if (res?.success === false) {
        setCardError({ id: bankDoc.id, message: "Failed to delete bank. Try again." });
        return;
      }
      await reloadDocumentsList();
    } catch (err) {
      console.error(err);
      setCardError({ id: bankDoc.id, message: "Failed to delete bank. Try again." });
    } finally {
      setDeleting(null);
    }
  };


  return (
    <>
      <div className="mb-4">
        <div className=" border rounded p-3">
          <h6>
            {doc.title} {doc.is_mandatory && <span className="text-danger">*</span>}
          </h6>
          <p className="text-muted">{doc.description}</p>

          {/* Existing banks list */}
          {existingBanks.length > 0 ? (
            existingBanks.map((bankDoc) => (
              <Card key={bankDoc.id} className="mb-3 shadow-sm">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <div>
                      <h6 className="mb-1">{bankDoc.metadata?.bank_name || 'Others'} - <small className="text-muted">{bankDoc.metadata?.account_number}</small></h6>
                    </div>
                    {/* 🔴 Delete Icon */}
                    <Button
                      variant="link"
                      className="p-0 text-danger"
                      title="Delete bank and all statements"
                      onClick={() => handleDeleteBank(bankDoc)}
                      disabled={deleting === bankDoc.id}
                    >
                      {deleting === bankDoc.id ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        <BsTrash size={18} />
                      )}
                    </Button>
                  </div>

                  {/* Upload new statements */}
                  <Form.Group className="mt-3">
                    <Form.Control
                      className="file-input--hide-chosen-text"
                      type="file"
                      aria-label="Upload statements"
                      multiple
                      onChange={(e) => handleUploadFiles(e, bankDoc)}
                      ref={(el) => (fileInputsRef.current[bankDoc.id] = el)}
                      disabled={!!uploadingId}
                      data-testid="fincobox-upload-bank-statement-input"
                    />
                    {uploadingId === bankDoc.id && (
                      <div className="d-flex justify-content-center align-items-center mt-2 text-muted" role="status">
                        <Spinner animation="border" size="sm" />
                        <span className="ms-2">Uploading file...</span>
                      </div>
                    )}
                    {cardError.id === bankDoc.id && cardError.message && (
                      <p className="text-danger small mt-2 mb-0" role="alert">{cardError.message}</p>
                    )}
                  </Form.Group>

                  {/* Uploaded files */}
                  <div className="mt-2">
                    {(bankDoc.document_files || []).length > 0 && (
                      <span data-testid="fincobox-upload-bank-statement-uploaded" aria-hidden="true" className="visually-hidden">✓</span>
                    )}
                    <DocumentFiles files={bankDoc.document_files || []} reloadDocumentsList={reloadDocumentsList} />
                  </div>
                </Card.Body>
              </Card>
            ))
          ) : (
            <p className="text-muted">No banks added yet.</p>
          )}

          {/* Add new bank form */}
          {adding ? (
            <Card className="mt-3 p-3 shadow-sm">
              <Row>
                <Col md={5}>
                  <Form.Group className="mb-3" controlId="add-bank-name">
                    <Form.Label>Bank Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="bank_name"
                      placeholder="Enter bank name"
                      value={newBank.bank_name}
                      onChange={handleChange}
                      data-testid="fincobox-upload-bank-name"
                    />
                  </Form.Group>
                </Col>
                <Col md={5}>
                  <Form.Group className="mb-3" controlId="add-bank-account-number">
                    <Form.Label>Account Number</Form.Label>
                    <Form.Control
                      type="text"
                      name="account_number"
                      placeholder="Enter account number"
                      value={newBank.account_number}
                      onChange={handleChange}
                      data-testid="fincobox-upload-bank-account"
                    />
                  </Form.Group>
                </Col>
              </Row>

              {error && <p className="text-danger small">{error}</p>}

              <div className="d-flex justify-content-end gap-2">
                <Button variant="secondary" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleSaveBank} disabled={saving} data-testid="fincobox-upload-save-bank">
                  {saving ? <Spinner animation="border" size="sm" /> : "Save Bank"}
                </Button>
              </div>
            </Card>
          ) : (
            <Button variant="outline-primary" onClick={handleAddClick} data-testid="fincobox-upload-add-bank">
              + Add Bank
            </Button>
          )}
        </div>

        {validationError && (
          <div className="text-danger small mb-4">
            {validationError}
          </div>
        )}
      </div>
    </>
  );
};

export default BankStatementsUploadForm;
